import { type EventName } from './tracker';

type MetaStandardEventName =
  | 'PageView'
  | 'Lead'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'Purchase'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'Schedule'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Contact'
  | 'Donate'
  | 'FindLocation'
  | 'CustomizeProduct';

type GAStandardEventName =
  | 'generate_lead'
  | 'begin_checkout'
  | 'complete_registration'
  | 'purchase'
  | 'view_item'
  | 'search'
  | 'page_view'
  | 'sign_up'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'add_payment_info'
  | 'add_shipping_info'
  | 'login'
  | 'select_item'
  | 'view_cart'
  | 'view_item_list'
  | 'remove_from_cart'
  | 'start_checkout'
  | 'select_promotion'
  | 'view_promotion'
  | 'join_group'
  | 'earn_virtual_currency'
  | 'spend_virtual_currency'
  | 'unlock_achievement'
  | 'tutorial_begin'
  | 'tutorial_complete'
  | 'level_start'
  | 'level_end'
  | 'level_up'
  | 'post_score'
  | 'ad_impression'
  | 'ad_click'
  | 'screen_view'
  | 'file_download'
  | 'video_start'
  | 'video_progress'
  | 'video_complete';

export const eventMapping = {
  // Map custom events to standard Meta Pixel events
  mapToMetaPixel: (customEvent: EventName): MetaStandardEventName => {
    const mapping: {
      [K in EventName]?: MetaStandardEventName;
    } = {
      // User journey events
      SignUp: 'Lead',
      CompleteRegistration: 'CompleteRegistration',
      PageView: 'PageView',

      // User engagement events
      Search: 'Search',
      BookAppointment: 'Schedule',
    };

    return mapping[customEvent] || 'PageView'; // Default fallback
  },

  // Map custom events to GA4 events
  mapToGA4: (customEvent: EventName): GAStandardEventName => {
    const mapping: {
      [K in EventName]?: GAStandardEventName;
    } = {
      // User journey events
      SignUp: 'generate_lead',
      CompleteRegistration: 'complete_registration',
      PageView: 'page_view',

      // User engagement events
      Search: 'search',
      BookAppointment: 'begin_checkout',
    };

    return mapping[customEvent] || 'page_view'; // Default fallback
  },

  // Get platform-specific parameters for Meta Pixel
  getMetaPixelParams: (
    event: string,
    customParams: Record<string, unknown>
  ) => {
    switch (event) {
      case 'Purchase':
        return {
          value: customParams.value || customParams.amount,
          currency: customParams.currency || 'USD',
          content_ids: customParams.serviceIds,
          content_type: customParams.contentType || 'service',
        };

      case 'InitiateCheckout':
        return {
          value: customParams.value,
          currency: customParams.currency || 'USD',
          content_ids: customParams.serviceIds,
          content_type: customParams.contentType || 'service',
        };

      case 'ViewContent':
        return {
          content_ids: customParams.serviceId
            ? [customParams.serviceId]
            : customParams.serviceIds,
          content_type: customParams.contentType || 'service',
          content_name: customParams.serviceName,
        };

      case 'CompleteRegistration':
        return {
          status: customParams.status || 'completed',
          registration_method: customParams.method || 'email',
        };

      case 'Search':
        return {
          search_string: customParams.query,
          content_type: customParams.contentType || 'service',
        };

      default:
        return customParams;
    }
  },

  // Get platform-specific parameters for GA4
  getGA4Params: (event: string, customParams: Record<string, unknown>) => {
    switch (event) {
      case 'purchase':
        return {
          currency: customParams.currency || 'USD',
          transaction_id: customParams.transactionId,
          value: customParams.value || customParams.amount,
          items: customParams.items || [
            {
              item_id: customParams.serviceId,
              item_name: customParams.serviceName,
              category: 'service',
              quantity: 1,
              price: customParams.value,
            },
          ],
        };

      case 'begin_checkout':
        return {
          currency: customParams.currency || 'USD',
          value: customParams.value,
          items: customParams.items || [
            {
              item_id: customParams.serviceId,
              item_name: customParams.serviceName,
              category: 'service',
              quantity: 1,
            },
          ],
        };

      case 'view_item':
        return {
          currency: customParams.currency || 'USD',
          value: customParams.value,
          items: [
            {
              item_id: customParams.serviceId,
              item_name: customParams.serviceName,
              category: 'service',
              quantity: 1,
            },
          ],
        };

      case 'generate_lead':
        return {
          lead_type: customParams.leadType || 'cleaner_signup',
          step_name: customParams.stepName,
        };

      case 'page_view':
        return {
          page_location: customParams.page_location || window.location.href,
          page_title: customParams.page_title || document.title,
          page_referrer: customParams.page_referrer || document.referrer,
        };

      case 'sign_up':
        return {
          method: customParams.method || 'email',
          user_type: customParams.userType || 'cleaner',
        };

      case 'search':
        return {
          search_term: customParams.query,
          search_type: customParams.searchType || 'service',
        };

      default:
        return customParams;
    }
  },
};
